import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component that scrolls the window to the top when the route changes
 * This component should be placed inside the Router component in App.jsx
 * 
 * It will NOT scroll to top when:
 * - Opening a menu item (URL contains ?item=)
 * - Closing a menu item (URL changes from having ?item= to not having it)
 * 
 * @returns {null} This component doesn't render anything
 */
export default function ScrollToTop() {
  const { pathname, search } = useLocation();
  const prevSearchRef = useRef(search);
  
  useEffect(() => {
    // Parse search params
    const currentParams = new URLSearchParams(search);
    const prevParams = new URLSearchParams(prevSearchRef.current);
    
    // Check if this is a menu item navigation (opening or closing)
    const hasItemParam = currentParams.has('item');
    const hadItemParam = prevParams.has('item');
    
    // Don't scroll if:
    // 1. Opening a menu item (adding ?item=)
    // 2. Closing a menu item (removing ?item=)
    const isMenuItemNavigation = hasItemParam || hadItemParam;
    
    // Update the previous search ref for the next comparison
    prevSearchRef.current = search;
    
    // Only scroll to top for non-menu item navigation
    if (!isMenuItemNavigation) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant' // Use 'instant' instead of 'smooth' to avoid jarring UX when navigating
      });
    }
  }, [pathname, search]);

  return null;
}
